import { Phone, Target, Compass, HeartHandshake, Sparkles, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { CTAButton } from "@/components/shared/CTAButton";
import { Watermark } from "@/components/shared/Watermark";
import { MotionStagger, MotionStaggerItem, MotionReveal } from "@/components/shared/MotionReveal";
import { SEO, buildAttorneySchema, buildLegalServiceSchema } from "@/components/seo/SEO";
import { CaseEvaluationDialog } from "@/components/home/CaseEvaluationDialog";
import { FIRM, PAGE_HERO_IMAGES } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

const PILLAR_ICONS = [Target, Compass, HeartHandshake, Sparkles];

export default function About() {
  const { t, isSpanish } = useLanguage();
  const pillars = t("about.pillars");

  const subtitle = isSpanish
    ? `Desde ${FIRM.foundedYear}, Rinnou & Associates, P.C. ha ayudado a cientos de clientes en Dallas a resolver con confianza asuntos de inmigración, familia, negocios y planificación patrimonial.`
    : `Since ${FIRM.foundedYear}, Rinnou & Associates, P.C. has helped hundreds of clients across Dallas navigate immigration, family, business, and estate planning matters with confidence.`;

  const story1 = isSpanish
    ? `Nuestra firma fue fundada por Jouliana Rinnou en ${FIRM.foundedYear}. Jouliana y su familia emigraron a los Estados Unidos hace muchos años y vivieron en carne propia la dificultad e incertidumbre del sistema migratorio.`
    : `Our law firm was founded by Jouliana Rinnou in ${FIRM.foundedYear}. Jouliana and her family immigrated to the United States many years ago and experienced firsthand the difficulty and uncertainty of the immigration system.`;

  const story2 = isSpanish
    ? "Esa experiencia de vida le dio a Jouliana la determinación de hacer que el proceso legal sea menos estresante y más claro para cada cliente que cruza nuestras puertas. Enfoca su práctica en inmigración, negocios, derecho familiar y planificación patrimonial, y ha construido un enfoque de equipo en el que los clientes no solo contratan a una abogada, sino a toda una firma dedicada a su éxito."
    : "That life experience gave Jouliana the determination to make the legal process less stressful and more informed for every client who walks through our doors. She focuses her practice on immigration, business, family, and estate planning law — and has built a team approach where clients don't just hire an attorney, they hire an entire firm dedicated to their success.";

  return (
    <>
      <SEO
        title={t("seo.about.title")}
        description={t("seo.about.description")}
        path="/about"
        jsonLd={[buildAttorneySchema(), buildLegalServiceSchema()]}
      />

      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden py-20 sm:py-24">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(11,18,32,0.94) 0%, rgba(11,18,32,0.88) 55%, rgba(11,18,32,0.97) 100%), url(${PAGE_HERO_IMAGES.about})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />
        <Watermark word="Our Story" position="center" className="text-white" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">{t("about.eyebrow")}</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-white text-balance">
            {t("about.heading1")} <span className="italic text-[color:var(--gold-400)]">{t("about.headingEmphasis")}</span> {t("about.headingEnd")}
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-base sm:text-lg">{subtitle}</p>
        </div>
      </header>

      <section className="relative py-16 sm:py-20 lg:py-28 bg-white overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_70px_rgba(11,18,32,0.15)] ring-1 ring-slate-100">
              <img
                src="/images/attorney-jouliana-rinnou.jpg"
                alt={`${FIRM.founder}, ${FIRM.founderTitle} at Rinnou & Associates, P.C.`}
                className="w-full h-[460px] object-cover object-top"
              />
              <div className="bg-[color:var(--navy-950)] px-6 py-4" data-testid="about-attorney-caption">
                <p className="font-heading text-lg text-white leading-tight">{FIRM.founder}</p>
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--gold-400)] mt-0.5">{t("about.founderTitle")}</p>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-[color:var(--gold-500)] grid place-items-center shadow-[0_16px_40px_rgba(184,154,61,0.35)] -rotate-3">
              <Quote className="h-8 w-8 text-[color:var(--navy-950)]" />
            </div>
          </motion.div>
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)] mb-1">{FIRM.founder}</h2>
            <p className="text-sm uppercase tracking-[0.15em] text-[color:var(--gold-500)] mb-6">{t("about.founderTitle")}</p>
            <p className="text-slate-600 leading-7 mb-4">{story1}</p>
            <p className="text-slate-600 leading-7 mb-6">{story2}</p>
            <CaseEvaluationDialog trigger={<CTAButton variant="primary">{t("about.scheduleCta")}</CTAButton>} />
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28 bg-[color:var(--sand)] overflow-hidden">
        <Watermark word="Values" position="top-right" className="text-[color:var(--navy-950)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("about.pillarsEyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium text-[color:var(--navy-950)]">
              {t("about.pillarsHeading1")} <span className="italic text-[color:var(--gold-500)]">{t("about.pillarsHeadingEmphasis")}</span> {t("about.pillarsHeadingEnd")}
            </h2>
          </MotionReveal>
          <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {pillars.map((p, idx) => {
              const PillarIcon = PILLAR_ICONS[idx] || Target;
              return (
                <MotionStaggerItem key={p.title}>
                  <div className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-8 h-full hover:shadow-[0_18px_50px_rgba(11,18,32,0.08)] transition-shadow duration-300">
                    <div className="h-12 w-12 rounded-2xl bg-[color:var(--navy-950)] grid place-items-center mb-4">
                      <PillarIcon className="h-5 w-5 text-[color:var(--gold-400)]" />
                    </div>
                    <h3 className="font-heading text-xl text-[color:var(--navy-950)] mb-2">{p.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  </div>
                </MotionStaggerItem>
              );
            })}
          </MotionStagger>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 bg-[color:var(--navy-950)] hero-noise overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MotionReveal>
            <h2 className="font-heading text-2xl sm:text-3xl text-white">
              {t("about.finalHeading1")} <span className="italic text-[color:var(--gold-400)]">{t("about.finalHeadingEmphasis")}</span>
            </h2>
            <p className="mt-3 text-slate-300">{t("about.finalSubtitle")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href={`tel:${FIRM.phoneTel}`}>
                <CTAButton variant="brass" icon={Phone}>{FIRM.phoneDisplay}</CTAButton>
              </a>
            </div>
          </MotionReveal>
        </div>
      </section>
    </>
  );
}
